/**
 * LinkSearchStep — Step 2 of the New Import wizard (FR-03.3).
 *
 * A debounced ID/name typeahead to link a Thrive search
 * (`search_id_thrive`). REQUIRED for the MVP (every import is scoped to a
 * search — Compare/Export depend on it); the page gates the submit CTA on a
 * selection. The option list comes from Thrive via
 * `GET /api/web/v2/searches?q=` (status `Open` or `Business Development`,
 * plus the dev/local TEST whitelist pinned to the top, which is offered at any
 * status). Selection is persisted on the imports row at
 * /complete.
 *
 * Replaces the earlier plain `<select>` (2026-07-02 PRD:
 * searches-picker-open-filter-typeahead) — the old unfiltered `<select>`
 * fetched an arbitrary, unpaginated page-1 slice of ~4,353 Thrive searches,
 * so known TEST fixtures had only a small chance of appearing at all.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px scale.
 * No inline styles, no arbitrary `text-[Npx]`.
 */

import type { ReactNode } from "react";

import {
  SearchTypeahead,
  type SearchTypeaheadOption,
  type SearchTypeaheadResult,
} from "./SearchTypeahead";

export type { SearchTypeaheadOption, SearchTypeaheadResult };

export interface LinkSearchStepProps {
  /** Selected search id, or `null` when nothing is linked yet. */
  value: string | null;
  /** Selected search's name (paired with `value`). */
  valueName: string | null;
  onChange: (searchId: string | null, searchName: string | null) => void;
  /** Returns the current page of options for `query` (`""` = initial view). */
  search: (query: string) => Promise<SearchTypeaheadResult>;
  /** The field's label. The Thrive path says the link stays in NU Assist
   *  (Jared, 2026-10-20); the upload path keeps "Link to Search". */
  label?: ReactNode;
}

export function LinkSearchStep({
  value,
  valueName,
  onChange,
  search,
  label = "Link to Search",
}: LinkSearchStepProps) {
  return (
    <div data-testid="link-search-step" className="flex flex-col gap-3">
      <label htmlFor="link-search" className="text-label text-text">
        {label} <span className="text-warning">*</span>
      </label>
      <div id="link-search">
        {/* Opens on focus like the wizard's source picker, so the two pickers
            of one wizard behave alike (Jose, local testing, 2026-10-20). */}
        <SearchTypeahead
          value={value}
          valueName={valueName}
          onChange={onChange}
          search={search}
          openOnFocus
        />
      </div>
      <p className="text-caption text-text-secondary">
        Required — Compare and Export are scoped to this Thrive search.
      </p>
    </div>
  );
}
